import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { computePeacMonthly, PLM_MAX_RATIO } from './peacLeasing';
import './ConfiguratorAriane.css';
import './RentabiliteAriane.css';

function RentabiliteAriane() {
  // ============================================
  // ÉTATS (STATE) — configurateur
  // ============================================
  const [selectedMachine, setSelectedMachine] = useState('pam4-illimite');
  const [purchaseType, setPurchaseType] = useState('lease');
  const [leaseDuration, setLeaseDuration] = useState('48');
  const [firstLeasePayment, setFirstLeasePayment] = useState(0);
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);
  const [showFourchette, setShowFourchette] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedShotPack, setSelectedShotPack] = useState(null);
  const [formationIPLCount, setFormationIPLCount] = useState(1);
  const [formationLaserCount, setFormationLaserCount] = useState(1);
  const [selectedFlashPack, setSelectedFlashPack] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({
    warranty: false,
    warrantyMonthly: false,
    applicateurHR: false,
    applicateurHRMonthly: false,
    applicateurSR: false,
    applicateurSRMonthly: false,
    emboutIPLV64: false,
    installation: false,
    formationLaser: false,
    formationLaserIPL: false
  });

  // ============================================
  // ÉTATS (STATE) — rentabilité
  // ============================================
  const [prixSeance, setPrixSeance] = useState(250);
  const [clientsSemaine, setClientsSemaine] = useState(2);
  const [amortMonths] = useState(60);
  const [simMode] = useState('parSeance'); // 'parSeance' | 'parBudget'
  const [budgetMensuel, setBudgetMensuel] = useState(0);
  const [manualPrixOverride, setManualPrixOverride] = useState(null);
  const [isCalculated, setIsCalculated] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const calcTimerRef = useRef(null);

  // Réinitialise tout quand les inputs changent
  useEffect(() => {
    setManualPrixOverride(null);
    setIsCalculated(false);
    setIsCalculating(false);
    clearTimeout(calcTimerRef.current);
  }, [prixSeance, clientsSemaine]);

  const handleCalculer = () => {
    setIsCalculating(true);
    calcTimerRef.current = setTimeout(() => {
      setIsCalculating(false);
      setIsCalculated(true);
    }, 2000);
  };

  // ============================================
  // DONNÉES STATIQUES
  // ============================================
  const media = [
    { type: 'video', url: 'https://www.youtube.com/embed/VdztHSC6Ivo?start=11&autoplay=1&mute=1&loop=1&playlist=VdztHSC6Ivo&controls=0&modestbranding=1&rel=0&showinfo=0&iv_load_policy=3' }
  ];

  const options = {
    warranty: {
      name: 'Extension garantie Laser Ariane 5 ans (2+3)**',
      price: 3000,
      monthlyPrice: 50,
      monthlyDuration: '5 ans',
      hasMonthlyOption: true,
      includedInLease: true
    },
    applicateurHR: {
      name: 'Applicateur HR Legacy ***',
      price: 2500,
      monthlyPrice: 50,
      monthlyDuration: '5 ans',
      hasMonthlyOption: true,
      includedInLease: false,
      description: 'Applicateur HR (hair removal) : pour des soins photo pilaires d\'excellence (efficace sur tous les types de poils et tous les phototypes) et brevet exclusif poils blonds et blancs garantissant l\'élimination des poils clairs et peu pigmentés.'
    },
    applicateurSR: {
      name: 'Applicateur SR Legacy ***',
      price: 2500,
      monthlyPrice: 50,
      monthlyDuration: '5 ans',
      hasMonthlyOption: true,
      includedInLease: false,
      description: 'Applicateur SR (skin rejuvenation) : soins de photorajeunissement (soin anti-âge, anti-taches et anti-rougeurs capillaires).'
    },
    emboutIPLV64: {
      name: 'Embout IPL Petite Zone V64',
      price: 1155,
      hasMonthlyOption: false,
      includedInLease: false,
      description: 'Embout effilé pour traiter les petites zones en soin photo-pilaires et de photorajeunissement (oreilles, nez...).'
    },
    installation: {
      name: 'Installation Laser Ariane',
      price: 490,
      hasMonthlyOption: false,
      includedInLease: true
    },
    formationLaser: {
      name: 'Formation socle Laser',
      price: 1500,
      hasMonthlyOption: false,
      includedInLease: true,
      perPerson: true
    },
    formationLaserIPL: {
      name: 'Formation socle Laser + IPL',
      price: 1900,
      hasMonthlyOption: false,
      includedInLease: false,
      perPerson: true
    }
  };

  const shotPacks = {
    '5000': { name: '5 000 tirs', price: 450 },
    '10000': { name: '10 000 tirs', price: 800 },
    '15000': { name: '15 000 tirs', price: 1050 },
    '30000': { name: '30 000 tirs', price: 1800 },
    '60000': { name: '60 000 tirs', price: 3000 },
    '100000': { name: '100 000 tirs', price: 4000 }
  };

  const flashPacks = {
    '5000': { name: '5 000 flashs', price: 850 },
    '10000': { name: '10 000 flashs', price: 1200 },
    '15000': { name: '15 000 flashs', price: 1500 },
    '20000': { name: '20 000 flashs', price: 1800 },
    '30000': { name: '30 000 flashs', price: 2700 },
    '45000': { name: '45 000 flashs', price: 4050 },
    '60000': { name: '60 000 flashs', price: 4800 },
    '75000': { name: '75 000 flashs', price: 6000 }
  };

  const machines = {
    'pam4-illimite': {
      name: 'LASER ARIANE + PAM LASER 4 ILLIMITÉ',
      description: 'Laser Ariane avec capacité illimitée',
      price: '37 900,00 €',
      leaseBasePrice: 31900,
      isConnect: false
    },
    'pam4-connect': {
      name: 'LASER ARIANE + PAM LASER 4 CONNECT',
      description: 'Laser Ariane avec tirs limités',
      price: '28 900,00 €',
      leaseBasePrice: 22900,
      isConnect: true
    },
    'pam2-illimite': {
      name: 'LASER ARIANE + PAM LASER 2.5 ILLIMITÉ',
      description: 'Laser Ariane avec capacité illimitée',
      price: '35 900,00 €',
      leaseBasePrice: 28900,
      isConnect: false
    },
    'pam2-connect': {
      name: 'LASER ARIANE + PAM LASER 2.5 CONNECT',
      description: 'Laser Ariane avec tirs limités',
      price: '25 500,00 €',
      leaseBasePrice: 21900,
      isConnect: true
    }
  };

  const currentMachine = machines[selectedMachine];

  // ============================================
  // EFFETS
  // ============================================
  useEffect(() => {
    if (!currentMachine.isConnect) setSelectedShotPack(null);
  }, [selectedMachine, currentMachine.isConnect]);

  useEffect(() => {
    if (!selectedOptions.applicateurHR && !selectedOptions.applicateurSR) setSelectedFlashPack(null);
  }, [selectedOptions.applicateurHR, selectedOptions.applicateurSR]);

  useEffect(() => {
    if (purchaseType === 'purchase') setFirstLeasePayment(0);
  }, [purchaseType]);

  useEffect(() => {
    setFirstLeasePayment(0);
  }, [selectedMachine]);

  useEffect(() => {
    if (purchaseType === 'lease') setFormationLaserCount(0);
    else setFormationLaserCount(1);
  }, [purchaseType]);

  // ============================================
  // GESTIONNAIRES D'ÉVÉNEMENTS
  // ============================================
  const handleOptionToggle = (optionKey, isMonthly = false) => {
    const option = options[optionKey];
    const monthlyKey = `${optionKey}Monthly`;
    if (purchaseType === 'lease' && option.includedInLease) return;
    if (isMonthly) {
      setSelectedOptions(prev => ({ ...prev, [optionKey]: true, [monthlyKey]: !prev[monthlyKey] }));
    } else {
      setSelectedOptions(prev => ({
        ...prev,
        [optionKey]: !prev[optionKey],
        [monthlyKey]: !prev[optionKey] ? false : prev[monthlyKey]
      }));
    }
  };

  // ============================================
  // FONCTIONS DE CALCUL — configurateur
  // ============================================
  const calculateOptionsTotal = () => {
    let total = 0;
    Object.keys(options).forEach(key => {
      const option = options[key];
      if (purchaseType === 'lease' && key === 'formationLaser') {
        if (formationLaserCount > 0) total += option.price * formationLaserCount;
        return;
      }
      if (purchaseType === 'lease' && option.includedInLease) return;
      if (selectedOptions[key]) {
        const monthlyKey = `${key}Monthly`;
        if (option.hasMonthlyOption && selectedOptions[monthlyKey]) {
          total += option.monthlyPrice;
        } else {
          let multiplier = 1;
          if (option.perPerson) multiplier = key === 'formationLaser' ? formationLaserCount : formationIPLCount;
          total += option.price * multiplier;
        }
      }
    });
    return total;
  };

  const getMonthlyOptionsTotal = () => {
    let total = 0;
    Object.keys(options).forEach(key => {
      const option = options[key];
      if (purchaseType === 'lease' && option.includedInLease) return;
      if (selectedOptions[key] && option.hasMonthlyOption) {
        const monthlyKey = `${key}Monthly`;
        if (selectedOptions[monthlyKey]) total += option.monthlyPrice;
      }
    });
    return total;
  };

  const calculateShotPackPrice = () => (!selectedShotPack || !currentMachine.isConnect) ? 0 : shotPacks[selectedShotPack].price;
  const calculateFlashPackPrice = () => {
    if (!selectedFlashPack) return 0;
    if (!selectedOptions.applicateurHR && !selectedOptions.applicateurSR) return 0;
    return flashPacks[selectedFlashPack].price;
  };

  // Mensualité selon le barème PEAC (loyers suivants si PLM)
  const getLeaseMonthly = (duration, firstPayment = 0) =>
    computePeacMonthly(currentMachine.leaseBasePrice, parseInt(duration), firstPayment);

  const getMaxFirstLeasePayment = () => Math.floor(currentMachine.leaseBasePrice * PLM_MAX_RATIO);

  const getEffectiveLeaseDuration = () => {
    if (firstLeasePayment > 0) return leaseDuration === '48' ? 47 : 59;
    return parseInt(leaseDuration);
  };

  const getBasePrice = () => {
    if (purchaseType === 'purchase') {
      return parseFloat(currentMachine.price.replace(/\s/g, '').replace(',', '.').replace('€', ''));
    }
    return getLeaseMonthly(leaseDuration, firstLeasePayment);
  };

  const formatPrice = (price) => {
    const absPrice = Math.abs(price);
    const [integerPart, decimalPart] = absPrice.toFixed(2).split('.');
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return `${formattedInteger},${decimalPart}`;
  };

  const getTotalPrice = () => {
    const basePrice = getBasePrice();
    const shotPackPrice = calculateShotPackPrice();
    const flashPackPrice = calculateFlashPackPrice();
    if (purchaseType === 'purchase') {
      const optionsTotal = calculateOptionsTotal();
      const total = basePrice + optionsTotal + shotPackPrice + flashPackPrice;
      return formatPrice(total) + ' €';
    } else {
      const monthlyOptions = getMonthlyOptionsTotal();
      const total = basePrice + monthlyOptions;
      return formatPrice(total) + ' €/mois';
    }
  };

  const getDisplayPrice = () => getTotalPrice();

  // ============================================
  // FONCTIONS DE CALCUL — rentabilité
  // ============================================

  /**
   * Coût mensuel de la machine selon le mode d'acquisition :
   * - Location : mensualité (base + options mensuelles)
   * - Achat : prix total amorti sur amortMonths
   */
  const getCoutMensuelMachine = () => {
    if (purchaseType === 'lease') {
      return getBasePrice() + getMonthlyOptionsTotal();
    } else {
      const basePrice = getBasePrice();
      const optionsTotal = calculateOptionsTotal();
      const shotPackPrice = calculateShotPackPrice();
      const flashPackPrice = calculateFlashPackPrice();
      const totalPrice = basePrice + optionsTotal + shotPackPrice + flashPackPrice;
      return totalPrice / amortMonths;
    }
  };

  /** Chiffre d'affaires mensuel estimé : prix × clients/sem × 4 semaines */
  const getCaMensuel = () => prixSeance * clientsSemaine * 4;

  /** Résultat brut mensuel = CA - coût machine */
  const getResultatMensuel = () => getCaMensuel() - getCoutMensuelMachine();

  /** Résultat brut annuel */
  const getResultatAnnuel = () => getResultatMensuel() * 12;

  /** Nombre de séances nécessaires par mois pour couvrir le coût machine */
  const getNbSeancesPourCouvrir = () => {
    if (prixSeance <= 0) return 0;
    return Math.ceil(getCoutMensuelMachine() / prixSeance);
  };

  /** Nombre total de séances mensuelles (clients × 4 semaines) */
  const getSeancesTotal = () => Math.round(clientsSemaine * 4);

  /** Point mort en mode achat : nb de mois pour récupérer l'investissement total */
  const getPointMortMois = () => {
    if (purchaseType !== 'purchase') return null;
    const caMensuel = getCaMensuel();
    if (caMensuel <= 0) return null;
    const basePrice = getBasePrice();
    const optionsTotal = calculateOptionsTotal();
    const shotPackPrice = calculateShotPackPrice();
    const flashPackPrice = calculateFlashPackPrice();
    const totalPrice = basePrice + optionsTotal + shotPackPrice + flashPackPrice;
    return Math.ceil(totalPrice / caMensuel);
  };

  // ============================================
  // FONCTIONS DE CALCUL — mode B (par budget)
  // ============================================

  /** Écart entre budget client et coût machine (positif = dans le budget) */
  const getBudgetEcart = () => budgetMensuel - getCoutMensuelMachine();

  /** Séances minimum pour couvrir le coût machine */
  const getSeancesMinBudget = () => {
    if (prixSeance <= 0) return 0;
    return Math.ceil(getCoutMensuelMachine() / prixSeance);
  };

  /** Clients minimum par semaine pour couvrir le coût machine */
  const getClientsMinBudget = () => Math.ceil(getSeancesMinBudget() / 4);

  /**
   * Prix affiché au client : loyer réel (minimum) → loyer × 1.20 (maximum).
   * Le +20% n'est atteint que quand le CA = 3× le loyer (forte rentabilité).
   * En dessous, la progression est lente et proportionnelle aux revenus du client.
   */
  const getPrixClientAffiche = () => {
    const cout = getCoutMensuelMachine();
    const hasData = prixSeance > 0 && clientsSemaine > 0;
    if (!hasData || purchaseType !== 'lease') return cout;
    const ca = getCaMensuel();
    // pct progresse de 0 à 1 quand CA va de cout à 3×cout
    const pct = Math.min(1, Math.max(0, (ca - cout) / (cout * 3)));
    return Math.round(cout * (1 + 0.20 * pct) / 10) * 10;
  };

  // ============================================
  // DÉRIVÉS
  // ============================================

  const hasRentabiliteData = simMode === 'parSeance'
    ? (prixSeance > 0 && clientsSemaine > 0)
    : (budgetMensuel > 0);

  const resultatMensuel = getResultatMensuel();
  const isPositif = resultatMensuel >= 0;
  const budgetEcart = getBudgetEcart();
  const isBudgetOk = budgetEcart >= 0;

  // Vue client : prix ajusté + résultat correspondant
  // manualPrixOverride permet de forcer manuellement le loyer depuis la fourchette
  const prixClientAffiche = manualPrixOverride !== null ? manualPrixOverride : getPrixClientAffiche();
  const resultatClientMensuel = getCaMensuel() - prixClientAffiche;
  const isResultatClientPositif = resultatClientMensuel >= 0;

  // Fourchette commerciale : loyer = min, loyer × 1.20 = objectif
  const plageMin = getCoutMensuelMachine();
  const plageMax = plageMin * 1.2;

  // ============================================
  // RENDU JSX
  // ============================================
  return (
    <div className="configurator-ariane rentabilite-mode">
      {/* Bandeau */}
      <div className="promo-banner rentabilite-banner">
        Simulateur de rentabilité — Laser Ariane
      </div>

      {/* Header */}
      <header className="configurator-header">
        <Link to="/" className="back-button">← Retour</Link>
        <h2>Rentabilité LASER ARIANE</h2>
        <Link to="/configurateur-ariane" className="back-button switch-link">Voir le configurateur →</Link>
      </header>

      {/* Layout principal */}
      <div className="configurator-layout">

        {/* ===== COLONNE GAUCHE : Galerie ===== */}
        <div className="image-section">
          <div className="image-container">
            {media.map((item, index) => (
              item.type === 'video' ? (
                <iframe
                  key={index}
                  src={item.url}
                  title="LASER ARIANE Video"
                  className={`machine-video ${index === currentImageIndex ? 'active' : ''}`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <img
                  key={index}
                  src={item.url}
                  alt={`LASER ARIANE ${index + 1}`}
                  className={`machine-image ${index === currentImageIndex ? 'active' : ''}`}
                />
              )
            ))}
            <div className="gallery-indicators">
              {media.map((_, index) => (
                <button
                  key={index}
                  className={`indicator ${index === currentImageIndex ? 'active' : ''}`}
                  onClick={() => setCurrentImageIndex(index)}
                  aria-label={`Média ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* ===== COLONNE DROITE : Configurateur + Rentabilité ===== */}
        <div className="config-section">
          <div className="config-content">

            {/* ===== SECTION RENTABILITÉ (toujours en premier) ===== */}
            <section className="config-step rentability-section">
              <h3 className="step-title rentability-title">Simulation de rentabilité</h3>

              {/* ---- Onglets de mode (Par budget mensuel masqué pour l'instant) ---- */}

              {/* ---- Mode A : par séance & clients ---- */}
              {simMode === 'parSeance' && (
                <div className="rentability-inputs">
                  <div className="rentability-input-group">
                    <label className="rentability-label">Prix moyen facturé par séance</label>
                    <div className="rentability-input-wrapper">
                      <input
                        type="number"
                        className="rentability-input"
                        value={prixSeance || ''}
                        onChange={(e) => setPrixSeance(Math.max(0, Number(e.target.value)))}
                        placeholder="ex: 349"
                        min="0"
                        step="10"
                      />
                      <span className="rentability-unit">€</span>
                    </div>
                  </div>
                  <div className="rentability-input-group">
                    <label className="rentability-label">Nombre de clients par semaine</label>
                    <div className="rentability-input-wrapper">
                      <input
                        type="number"
                        className="rentability-input"
                        value={clientsSemaine || ''}
                        onChange={(e) => setClientsSemaine(Math.max(0, Number(e.target.value)))}
                        placeholder="ex: 5"
                        min="0"
                        step="1"
                      />
                      <span className="rentability-unit">clients</span>
                    </div>
                  </div>
                </div>
              )}

              {/* ---- Mode B : par budget mensuel ---- */}
              {simMode === 'parBudget' && (
                <div className="rentability-inputs">
                  <div className="rentability-input-group rentability-input-full">
                    <label className="rentability-label">Budget mensuel du client</label>
                    <div className="rentability-input-wrapper">
                      <input
                        type="number"
                        className="rentability-input rentability-input-large"
                        value={budgetMensuel || ''}
                        onChange={(e) => setBudgetMensuel(Math.max(0, Number(e.target.value)))}
                        placeholder="ex: 700"
                        min="0"
                        step="50"
                      />
                      <span className="rentability-unit">€/mois</span>
                    </div>
                  </div>
                  <div className="rentability-input-group">
                    <label className="rentability-label">Prix par séance (optionnel)</label>
                    <div className="rentability-input-wrapper">
                      <input
                        type="number"
                        className="rentability-input"
                        value={prixSeance || ''}
                        onChange={(e) => setPrixSeance(Math.max(0, Number(e.target.value)))}
                        placeholder="ex: 349"
                        min="0"
                        step="10"
                      />
                      <span className="rentability-unit">€</span>
                    </div>
                  </div>
                </div>
              )}

              {/* ---- Résultats Mode A ---- */}
              {simMode === 'parSeance' && (purchaseType !== 'lease' ? (
                <div className="rentability-placeholder">
                  <p>Passez en mode <strong>Location</strong> pour activer la simulation de rentabilité.</p>
                </div>
              ) : hasRentabiliteData ? (
                <>
                {/* ---- Vue client : présentation transparente ---- */}
                <div className="vue-client-card">
                  <div className="vue-client-header">
                    <span className="vue-client-badge">Simulation client</span>
                    <span className="vue-client-machine">{currentMachine.name} · Location {leaseDuration} mois</span>
                  </div>
                  <div className="vue-client-metrics">
                    <div className="vue-client-metric">
                      <span className="vue-client-metric-label">Loyer mensuel</span>
                      {!isCalculated ? (
                        <button
                          className={`calculer-btn-inline${isCalculating ? ' is-loading' : ''}`}
                          onClick={!isCalculating ? handleCalculer : undefined}
                          disabled={isCalculating}
                        >
                          {isCalculating ? (
                            <><span className="btn-spinner" />Calcul…</>
                          ) : 'Calculer'}
                        </button>
                      ) : (
                        <>
                          <span className="vue-client-metric-value">{formatPrice(prixClientAffiche)} €</span>
                          <span className="vue-client-metric-sub">par mois</span>
                        </>
                      )}
                    </div>
                    <div className="vue-client-sep">→</div>
                    <div className="vue-client-metric">
                      <span className="vue-client-metric-label">CA mensuel estimé</span>
                      <span className="vue-client-metric-value">+{formatPrice(getCaMensuel())} €</span>
                      <span className="vue-client-metric-sub">{prixSeance} € × {clientsSemaine} cl. × 4 sem.</span>
                    </div>
                    {isCalculated && (
                      <>
                        <div className="vue-client-sep">=</div>
                        <div className={`vue-client-metric ${isResultatClientPositif ? 'positif' : 'negatif'}`}>
                          <span className="vue-client-metric-label">Bénéfice mensuel</span>
                          <span className="vue-client-metric-value">
                            {isResultatClientPositif ? '+' : '−'}{formatPrice(Math.abs(resultatClientMensuel))} €
                          </span>
                          <span className="vue-client-metric-sub">
                            soit {isResultatClientPositif ? '+' : '−'}{formatPrice(Math.abs(resultatClientMensuel * 12))} €/an
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {isCalculated && <div className="rentability-results">
                  <div className="rent-card rent-card-ca">
                    <div className="rent-card-label">CA mensuel estimé</div>
                    <div className="rent-card-value">+{formatPrice(getCaMensuel())} €</div>
                    <div className="rent-card-detail">
                      {prixSeance} € × {clientsSemaine} clients × 4 sem.
                    </div>
                  </div>

                  <div className={`rent-card rent-card-resultat ${isResultatClientPositif ? 'positif' : 'negatif'}`}>
                    <div className="rent-card-label">Résultat brut mensuel</div>
                    <div className="rent-card-value">
                      {isResultatClientPositif ? '+' : '−'}{formatPrice(Math.abs(resultatClientMensuel))}<small>€/mois</small>
                    </div>
                    <div className="rent-card-detail">
                      Soit {isResultatClientPositif ? '+' : '−'}{formatPrice(Math.abs(resultatClientMensuel * 12))} €/an
                    </div>
                  </div>

                  <div className="rent-card rent-card-seances">
                    <div className="rent-card-label">Séances pour couvrir le coût</div>
                    <div className="rent-card-value">{getNbSeancesPourCouvrir()} séances</div>
                    <div className="rent-card-detail">
                      sur {getSeancesTotal()} séances/mois prévues
                    </div>
                  </div>

                  {/* Fourchette commerciale +20% — masquée par défaut */}
                  {isPositif && (
                    <div className="fourchette-toggle-row rent-card-plage-full">
                      <button
                        className="fourchette-toggle-btn"
                        onClick={() => setShowFourchette(!showFourchette)}
                      >
                        {showFourchette ? '▲ Masquer' : '⚙'}
                      </button>
                    </div>
                  )}
                  {isPositif && showFourchette && (
                    <div className="rent-card rent-card-plage rent-card-plage-full">
                      <div className="rent-card-label">Loyer mensuel facturé</div>

                      {/* Valeur courante mise en avant */}
                      <div className="rent-plage-current">
                        <span className="rent-plage-current-value">
                          {formatPrice(prixClientAffiche)} €
                          <span className="rent-plage-current-unit">/mois</span>
                        </span>
                        {manualPrixOverride !== null && (
                          <button
                            className="rent-plage-reset-btn"
                            onClick={() => setManualPrixOverride(null)}
                            title="Revenir au montant calculé automatiquement"
                          >
                            ↺ Auto
                          </button>
                        )}
                      </div>

                      {/* Slider interactif min → max */}
                      <div className="rent-plage-slider-wrap">
                        <span className="rent-plage-bound-label">{formatPrice(plageMin)} €</span>
                        <input
                          type="range"
                          className="rent-plage-slider"
                          min={Math.round(plageMin)}
                          max={Math.round(plageMax)}
                          step={10}
                          value={prixClientAffiche}
                          onChange={(e) => setManualPrixOverride(Number(e.target.value))}
                        />
                        <span className="rent-plage-bound-label rent-plage-bound-target">{formatPrice(plageMax)} €</span>
                      </div>

                      {/* Statut CA */}
                      <div className="rent-plage-status">
                        <span className="rent-plage-ca">CA client estimé : <strong>{formatPrice(getCaMensuel())} €/mois</strong></span>
                        {/* <span className={`rent-plage-badge ${isAbovePlage ? 'above' : 'within'}`}>
                          {isAbovePlage ? 'Objectif dépassé' : `${Math.round(plagePercent)}% de la fourchette`}
                        </span> */}
                      </div>
                    </div>
                  )}

                  {/* <div className={`rent-card rent-card-taux ${getTauxCouverture() >= 100 ? 'positif' : 'negatif'}`}>
                    <div className="rent-card-label">Taux de couverture</div>
                    <div className="rent-card-value">{Math.round(getTauxCouverture())} %</div>
                    <div className="rent-card-detail">
                      {getTauxCouverture() >= 100
                        ? `Excédent de ${Math.round(getTauxCouverture() - 100)} %`
                        : `Déficit de ${Math.round(100 - getTauxCouverture())} %`
                      }
                    </div>
                  </div> */}

                  {purchaseType === 'purchase' && getPointMortMois() && (
                    <div className="rent-card rent-card-pointmort">
                      <div className="rent-card-label">Retour sur investissement</div>
                      <div className="rent-card-value">{getPointMortMois()} mois</div>
                      <div className="rent-card-detail">
                        ≈ {(getPointMortMois() / 12).toFixed(1)} ans
                      </div>
                    </div>
                  )}
                </div>}
                </>
              ) : (
                <div className="rentability-placeholder">
                  <p>Renseignez le prix par séance et le nombre de clients pour déverrouiller la simulation complète.</p>
                </div>
              ))}

              {/* ---- Résultats Mode B ---- */}
              {simMode === 'parBudget' && (purchaseType !== 'lease' ? (
                <div className="rentability-placeholder">
                  <p>Passez en mode <strong>Location</strong> pour activer la simulation de rentabilité.</p>
                </div>
              ) : budgetMensuel > 0 ? (
                <div className="rentability-results">
                  <div className={`rent-card rent-card-budget ${isBudgetOk ? 'positif' : 'negatif'}`}>
                    <div className="rent-card-label">Budget vs coût machine</div>
                    <div className="rent-card-value">
                      {isBudgetOk ? '+' : '−'}{formatPrice(Math.abs(budgetEcart))} €
                    </div>
                    <div className="rent-card-detail">
                      {formatPrice(budgetMensuel)} € client · {formatPrice(getCoutMensuelMachine())} € machine
                    </div>
                  </div>

                  <div className="rent-card rent-card-ca">
                    <div className="rent-card-label">CA mensuel min. à générer</div>
                    <div className="rent-card-value">{formatPrice(getCoutMensuelMachine())} €</div>
                    <div className="rent-card-detail">Pour couvrir le coût machine</div>
                  </div>

                  {prixSeance > 0 && (
                    <div className="rent-card rent-card-seances">
                      <div className="rent-card-label">Séances min. / mois</div>
                      <div className="rent-card-value">{getSeancesMinBudget()} séances</div>
                      <div className="rent-card-detail">À {prixSeance} € / séance</div>
                    </div>
                  )}

                  {prixSeance > 0 && (
                    <div className="rent-card rent-card-pointmort">
                      <div className="rent-card-label">Clients min. / semaine</div>
                      <div className="rent-card-value">{getClientsMinBudget()} clients</div>
                      <div className="rent-card-detail">Pour atteindre l'équilibre</div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="rentability-placeholder">
                  <p>Entrez le budget mensuel du client pour voir instantanément la compatibilité avec la machine sélectionnée.</p>
                </div>
              ))}

              <p className="rentability-disclaimer">
                * Simulation indicative hors charges (consommables, personnel, loyer, taxes). Basée sur 4 semaines par mois.
              </p>
              <p className="rentability-disclaimer">
                Ne constitue pas une offre contractuelle.
              </p>
            </section>

            {/* ===== CONFIGURATEUR : visible uniquement si rentabilité renseignée ===== */}
            {hasRentabiliteData && (<>

            {/* Section 1 : Choix du modèle */}
            <section className="config-step">
              <h3 className="step-title">1. Choisissez votre modèle</h3>
              <div className="options-grid">
                {Object.entries(machines).map(([key, machine]) => (
                  <button
                    key={key}
                    className={`option-card ${selectedMachine === key ? 'selected' : ''}`}
                    onClick={() => setSelectedMachine(key)}
                  >
                    <h4>{machine.name}</h4>
                    <p>{machine.description}</p>
                    <span className="option-price">{machine.price}</span>
                  </button>
                ))}
              </div>
              {currentMachine.isConnect && (
                <div className="connect-info-notice">
                  <p>L'applicateur laser Connect est mis à la disposition du Client dans le cadre d'un contrat d'usage Connect défini par Eurofeedback.</p>
                </div>
              )}
            </section>

            {/* Section 2 : Mode d'acquisition + amortissement (achat) */}
            <section className="config-step">
              <h3 className="step-title">2. Mode d'acquisition</h3>
              <div className="toggle-group">
                <button
                  className={`toggle-button ${purchaseType === 'purchase' ? 'active' : ''}`}
                  onClick={() => setPurchaseType('purchase')}
                >
                  <div className="toggle-icon">💳</div>
                  <div className="toggle-label">Achat</div>
                  <div className="toggle-description">Propriété immédiate</div>
                </button>
                <button
                  className={`toggle-button ${purchaseType === 'lease' ? 'active' : ''}`}
                  onClick={() => setPurchaseType('lease')}
                >
                  <div className="toggle-icon">📅</div>
                  <div className="toggle-label">Location</div>
                  <div className="toggle-description">Flexibilité financière</div>
                </button>
              </div>
            </section>

            {/* Section 3 : Durée de location */}
            {purchaseType === 'lease' && (
              <section className="config-step lease-duration">
                <h3 className="step-title">3. Durée de location</h3>
                <div className="toggle-group">
                  <button
                    className={`toggle-button ${leaseDuration === '48' ? 'active' : ''}`}
                    onClick={() => setLeaseDuration('48')}
                  >
                    <div className="toggle-label">48 mois</div>
                    <div className="toggle-price">{formatPrice(getLeaseMonthly('48'))} €/mois</div>
                    <div className="toggle-description">4 ans de location</div>
                  </button>
                  <button
                    className={`toggle-button ${leaseDuration === '60' ? 'active' : ''}`}
                    onClick={() => setLeaseDuration('60')}
                  >
                    <div className="toggle-label">60 mois</div>
                    <div className="toggle-price">{formatPrice(getLeaseMonthly('60'))} €/mois</div>
                    <div className="toggle-description">5 ans de location</div>
                  </button>
                </div>
                <div className="first-lease-payment">
                  <label className="first-lease-label">Premier loyer majoré (optionnel)</label>
                  <p className="first-lease-description">Versé au démarrage, il réduit les loyers suivants (30 % maximum du montant financé).</p>
                  <div className="first-lease-input-container">
                    <input
                      type="number"
                      className="first-lease-input"
                      value={firstLeasePayment || ''}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        const maxValue = getMaxFirstLeasePayment();
                        setFirstLeasePayment(Math.min(Math.max(0, value), maxValue));
                      }}
                      placeholder="0"
                      min="0"
                      max={getMaxFirstLeasePayment()}
                      step="500"
                    />
                    <span className="first-lease-currency">€</span>
                  </div>
                  {firstLeasePayment > 0 && (
                    <p className="first-lease-result">
                      Nouvelle mensualité : <strong>{formatPrice(getBasePrice())} €/mois sur {getEffectiveLeaseDuration()} mois</strong>
                    </p>
                  )}
                </div>
              </section>
            )}

            {/* Options générales */}
            <section className="config-step">
              <h3 className="step-title">
                {purchaseType === 'lease' ? '4. Options' : '3. Options'}
              </h3>
              <div className="options-list">
                {Object.entries(options)
                  .filter(([key]) => !['applicateurHR', 'applicateurSR', 'emboutIPLV64'].includes(key))
                  .map(([key, option]) => {
                    const isIncludedInLease = purchaseType === 'lease' && option.includedInLease;
                    const isDisabledByOtherFormation =
                      (key === 'formationLaser' && selectedOptions.formationLaserIPL) ||
                      (key === 'formationLaserIPL' && selectedOptions.formationLaser);
                    return (
                      <div
                        key={key}
                        className={`option-item ${isIncludedInLease ? 'included' : ''} ${
                          selectedOptions[key] || isIncludedInLease ? 'selected' : ''
                        } ${isDisabledByOtherFormation ? 'disabled' : ''}`}
                        onClick={() => !isIncludedInLease && !isDisabledByOtherFormation && handleOptionToggle(key, false)}
                      >
                        <div className="option-checkbox">
                          <input
                            type="checkbox"
                            checked={isIncludedInLease || selectedOptions[key]}
                            disabled={isIncludedInLease || isDisabledByOtherFormation}
                            onChange={() => {}}
                          />
                        </div>
                        <div className="option-info">
                          <div className="option-name-container">
                            <div className="option-name">{option.name}</div>
                            {isDisabledByOtherFormation && (
                              <div className="option-disabled-note">Non cumulable avec l'autre formation</div>
                            )}
                          </div>
                          <div className="option-price-container">
                            {!option.perPerson && (
                              <div className="option-price">{formatPrice(option.price)} €</div>
                            )}
                            {option.perPerson && (
                              <div className="person-count-selector" onClick={(e) => e.stopPropagation()}>
                                {purchaseType === 'lease' && key === 'formationLaser' && (
                                  <div className="person-included-note">1 personne incluse</div>
                                )}
                                <div className="person-count-row">
                                  <label>{purchaseType === 'lease' && key === 'formationLaser' ? 'Personnes supplémentaires' : 'Nombre de personnes'}</label>
                                  <select
                                    value={key === 'formationLaser' ? formationLaserCount : formationIPLCount}
                                    onChange={(e) => key === 'formationLaser'
                                      ? setFormationLaserCount(parseInt(e.target.value))
                                      : setFormationIPLCount(parseInt(e.target.value))
                                    }
                                  >
                                    {(purchaseType === 'lease' && key === 'formationLaser'
                                      ? [0, 1, 2, 3, 4, 5, 6, 7, 8, 9]
                                      : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
                                    ).map(n => <option key={n} value={n}>{n}</option>)}
                                  </select>
                                </div>
                                <div className="person-price-row">
                                  {purchaseType === 'lease' && key === 'formationLaser' ? (
                                    formationLaserCount > 0 ? (
                                      <>{formatPrice(option.price)} €/pers. × {formationLaserCount} = <strong>{formatPrice(option.price * formationLaserCount)} €</strong></>
                                    ) : <strong>Inclus</strong>
                                  ) : (
                                    <>{formatPrice(option.price)} €/pers. × {key === 'formationLaser' ? formationLaserCount : formationIPLCount} = <strong>{formatPrice(option.price * (key === 'formationLaser' ? formationLaserCount : formationIPLCount))} €</strong></>
                                  )}
                                </div>
                              </div>
                            )}
                            {option.hasMonthlyOption && !isIncludedInLease && (
                              <div
                                className={`monthly-toggle ${selectedOptions[`${key}Monthly`] ? 'active' : ''}`}
                                onClick={(e) => { e.stopPropagation(); handleOptionToggle(key, true); }}
                              >
                                <input type="checkbox" checked={selectedOptions[`${key}Monthly`]} onChange={() => {}} />
                                <span>{option.monthlyPrice} €/mois</span>
                              </div>
                            )}
                            {isIncludedInLease && <div className="option-included-badge">Inclus</div>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
              <div className="options-footnotes">
                <p>** L'extension de garantie couvre l'appareil pour une durée de 5 ans : (2) + 3 ans.</p>
              </div>
            </section>

            {/* Pack de tirs laser (Connect) */}
            {currentMachine.isConnect && (
              <section className="config-step">
                <h3 className="step-title">
                  {purchaseType === 'lease' ? '6. Pack de tirs laser' : '5. Pack de tirs laser'}
                </h3>
                <div className="shot-pack-info">
                  <p>L'applicateur Laser Ariane {selectedMachine.includes('pam2') ? '2.5' : '4'} Connect est livré avec 5 000 tirs inclus.</p>
                </div>
                <div className="shot-pack-selector">
                  <label className="shot-pack-label">Pack de tirs supplémentaires</label>
                  <select
                    className="shot-pack-dropdown"
                    value={selectedShotPack || ''}
                    onChange={(e) => setSelectedShotPack(e.target.value || null)}
                  >
                    <option value="">Sélectionnez un pack de tirs</option>
                    {Object.entries(shotPacks).map(([key, pack]) => (
                      <option key={key} value={key}>{pack.name} - {formatPrice(pack.price)} €</option>
                    ))}
                  </select>
                </div>
              </section>
            )}

            {/* Applicateur IPL */}
            <section className="config-step">
              <h3 className="step-title">
                {purchaseType === 'lease'
                  ? (currentMachine.isConnect ? '7. Applicateur IPL en option' : '6. Applicateur IPL en option')
                  : (currentMachine.isConnect ? '6. Applicateur IPL en option' : '5. Applicateur IPL en option')}
              </h3>
              <div className="options-list">
                {Object.entries(options)
                  .filter(([key]) => ['applicateurHR', 'applicateurSR', 'emboutIPLV64'].includes(key))
                  .map(([key, option]) => {
                    const isIncludedInLease = purchaseType === 'lease' && option.includedInLease;
                    return (
                      <div
                        key={key}
                        className={`option-item ${isIncludedInLease ? 'included' : ''} ${
                          selectedOptions[key] || isIncludedInLease ? 'selected' : ''
                        }`}
                        onClick={() => !isIncludedInLease && handleOptionToggle(key, false)}
                      >
                        <div className="option-checkbox">
                          <input
                            type="checkbox"
                            checked={isIncludedInLease || selectedOptions[key]}
                            disabled={isIncludedInLease}
                            onChange={() => {}}
                          />
                        </div>
                        <div className="option-info">
                          <div className="option-name">{option.name}</div>
                          {option.description && <div className="option-description">{option.description}</div>}
                          <div className="option-price-container">
                            <div className="option-price">{formatPrice(option.price)} €</div>
                            {option.hasMonthlyOption && !isIncludedInLease && (
                              <div
                                className={`monthly-toggle ${selectedOptions[`${key}Monthly`] ? 'active' : ''}`}
                                onClick={(e) => { e.stopPropagation(); handleOptionToggle(key, true); }}
                              >
                                <input type="checkbox" checked={selectedOptions[`${key}Monthly`]} onChange={() => {}} />
                                <span>{option.monthlyPrice} €/mois</span>
                              </div>
                            )}
                            {isIncludedInLease && <div className="option-included-badge">Inclus</div>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
              {(selectedOptions.applicateurHR || selectedOptions.applicateurSR) && (
                <div className="flash-pack-selector">
                  <label className="flash-pack-label">Pack flash initial</label>
                  <select
                    className="flash-pack-dropdown"
                    value={selectedFlashPack || ''}
                    onChange={(e) => setSelectedFlashPack(e.target.value || null)}
                  >
                    <option value="">Sélectionnez un pack de flashs</option>
                    {Object.entries(flashPacks).map(([key, pack]) => (
                      <option key={key} value={key}>{pack.name} - {formatPrice(pack.price)} €</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="options-footnotes">
                <p>*** Les applicateurs HR et SR legacy sont mis à la disposition du Client dans le cadre d'un contrat d'usage Connect.</p>
              </div>
            </section>

            <div style={{ height: '120px' }}></div>
            </>)}

          </div>
        </div>
      </div>

      {/* ===== BARRE STICKY RENTABILITÉ ===== */}
      <div className="config-summary-sticky">
        <div className="summary-bar" onClick={() => setIsDetailsExpanded(!isDetailsExpanded)}>

          {(isCalculated && !isCalculating && purchaseType === 'lease') ? (
            <div className="summary-main summary-rentabilite">
              {simMode === 'parSeance' ? (
                <>
                  <div className="summary-rentabilite-block">
                    <span className="summary-price-label">Loyer machine</span>
                    <span className="summary-price-value summary-cout">
                      {formatPrice(prixClientAffiche)} €/mois
                    </span>
                  </div>
                  <div className="summary-rentabilite-arrow">→</div>
                  <div className="summary-rentabilite-block">
                    <span className="summary-price-label">CA mensuel</span>
                    <span className="summary-price-value summary-ca">
                      +{formatPrice(getCaMensuel())} €
                    </span>
                  </div>
                  <div className="summary-rentabilite-arrow">→</div>
                  <div className="summary-rentabilite-block">
                    <span className="summary-price-label">Résultat mensuel</span>
                    <span className={`summary-price-value summary-resultat ${isResultatClientPositif ? 'positif' : 'negatif'}`}>
                      {isResultatClientPositif ? '+' : '−'}{formatPrice(Math.abs(resultatClientMensuel))} €/mois
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <div className="summary-rentabilite-block">
                    <span className="summary-price-label">Budget client</span>
                    <span className="summary-price-value summary-ca">
                      {formatPrice(budgetMensuel)} €/mois
                    </span>
                  </div>
                  <div className="summary-rentabilite-arrow">→</div>
                  <div className="summary-rentabilite-block">
                    <span className="summary-price-label">Coût machine</span>
                    <span className="summary-price-value summary-cout">
                      {formatPrice(getCoutMensuelMachine())} €/mois
                    </span>
                  </div>
                  <div className="summary-rentabilite-arrow">→</div>
                  <div className="summary-rentabilite-block">
                    <span className="summary-price-label">Écart</span>
                    <span className={`summary-price-value summary-resultat ${isBudgetOk ? 'positif' : 'negatif'}`}>
                      {isBudgetOk ? '+' : '−'}{formatPrice(Math.abs(budgetEcart))} €
                    </span>
                  </div>
                </>
              )}
            </div>
          ) : isCalculating ? (
            <div className="summary-main">
              <span className="summary-price-label">{currentMachine.name}</span>
              <div className="summary-sticky-loading">
                <div className="calculer-spinner calculer-spinner-sm" />
                <span className="summary-price-value summary-pending">Calcul en cours…</span>
              </div>
            </div>
          ) : (
            <div className="summary-main">
              <span className="summary-price-label">{currentMachine.name}</span>
              <span className="summary-price-value summary-pending">
                {hasRentabiliteData ? 'Cliquez sur "Calculer la rentabilité"' : 'Renseignez le prix / séance et le nombre de clients'}
              </span>
            </div>
          )}

          <button className="expand-toggle">
            <span className={`arrow ${isDetailsExpanded ? 'up' : 'down'}`}>▼</span>
          </button>
        </div>

        {isDetailsExpanded && (
          <div className="summary-details-expanded">
            <div className="summary-item">
              <span className="summary-label">Modèle:</span>
              <span className="summary-value">{currentMachine.name}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Mode:</span>
              <span className="summary-value">{purchaseType === 'purchase' ? 'Achat' : 'Location'}</span>
            </div>
            {purchaseType === 'lease' && (
              <div className="summary-item">
                <span className="summary-label">Durée:</span>
                <span className="summary-value">{getEffectiveLeaseDuration()} mois</span>
              </div>
            )}
            {purchaseType === 'purchase' && (
              <div className="summary-item">
                <span className="summary-label">Prix total:</span>
                <span className="summary-value">{getDisplayPrice()}</span>
              </div>
            )}
            {hasRentabiliteData && (
              <>
                <div className="summary-item summary-divider">
                  <span className="summary-label">Prix / séance:</span>
                  <span className="summary-value">{prixSeance} €</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Clients / semaine:</span>
                  <span className="summary-value">{clientsSemaine}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">CA mensuel:</span>
                  <span className="summary-value">+{formatPrice(getCaMensuel())} €</span>
                </div>
                {isCalculated && (
                  <>
                    <div className="summary-item">
                      <span className="summary-label">Coût machine/mois:</span>
                      <span className="summary-value">−{formatPrice(getCoutMensuelMachine())} €</span>
                    </div>
                    <div className={`summary-item ${isPositif ? 'summary-positif' : 'summary-negatif'}`}>
                      <span className="summary-label">Résultat mensuel:</span>
                      <span className="summary-value">
                        {isPositif ? '+' : '−'}{formatPrice(Math.abs(resultatMensuel))} €/mois
                      </span>
                    </div>
                    <div className={`summary-item ${isPositif ? 'summary-positif' : 'summary-negatif'}`}>
                      <span className="summary-label">Résultat annuel:</span>
                      <span className="summary-value">
                        {isPositif ? '+' : '−'}{formatPrice(Math.abs(getResultatAnnuel()))} €/an
                      </span>
                    </div>
                    {purchaseType === 'purchase' && getPointMortMois() && (
                      <div className="summary-item">
                        <span className="summary-label">Retour sur invest.:</span>
                        <span className="summary-value">{getPointMortMois()} mois</span>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default RentabiliteAriane;
